import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { Translate, getPaginationState, JhiPagination, JhiItemCount, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { ISearchQuery } from 'app/shared/model/search-query.model';
import { getEntities } from './search-query.reducer';
import { hasDataManagementAuthority } from 'app/shared/auth/private-route';

export const SearchQuery = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const isAllowedToManageData = useAppSelector(hasDataManagementAuthority);

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const searchQueryList = useAppSelector(state => state.searchQuery.entities);
  const loading = useAppSelector(state => state.searchQuery.loading);
  const totalItems = useAppSelector(state => state.searchQuery.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  return (
    <div>
      <h2 id="search-query-heading" data-cy="SearchQueryHeading">
        <Translate contentKey="coconApp.searchQuery.home.title">Search Queries</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="coconApp.searchQuery.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/search-query/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="coconApp.searchQuery.home.createLabel">Start a new Search Query</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {searchQueryList && searchQueryList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="coconApp.searchQuery.id">ID</Translate>
                </th>
                <th>
                  <Translate contentKey="coconApp.searchQuery.material">Material</Translate>
                </th>
                <th>
                  <Translate contentKey="coconApp.searchQuery.variant">Variant</Translate>
                </th>
                <th>
                  <Translate contentKey="coconApp.searchQuery.synthesis">Synthesis</Translate>
                </th>
                <th>
                  <Translate contentKey="coconApp.searchQuery.materialShape">Material Shape</Translate>
                </th>
                <th>
                  <Translate contentKey="coconApp.searchQuery.crystallinity">Crystallinity</Translate>
                </th>
                <th>
                  <Translate contentKey="coconApp.searchQuery.coatingOrFunctionalization">Coating Or Functionalization</Translate>{' '}
                </th>
                <th>
                  <Translate contentKey="coconApp.searchQuery.qualityScore">Quality Score</Translate>
                </th>
                <th>
                  <Translate contentKey="coconApp.searchQuery.materialSize">Material Size</Translate>
                </th>
                <th>
                  <Translate contentKey="coconApp.searchQuery.agglomerationSize">Agglomeration Size</Translate>
                </th>
                <th>
                  <Translate contentKey="coconApp.searchQuery.bet">BET</Translate>
                </th>
                <th>
                  <Translate contentKey="coconApp.searchQuery.animalOrCell">Animal Or Cell</Translate>
                </th>
                <th>
                  <Translate contentKey="coconApp.searchQuery.origins">Origins</Translate>
                </th>
                <th>
                  <Translate contentKey="coconApp.searchQuery.cellCultures">Cell Cultures</Translate>
                </th>
                <th>
                  <Translate contentKey="coconApp.searchQuery.tissueType">Tissue Type</Translate>
                </th>
                <th>
                  <Translate contentKey="coconApp.searchQuery.organ">Organ</Translate>
                </th>
                <th className="hand" onClick={sort('onlyGuidelineExperiments')}>
                  <Translate contentKey="coconApp.searchQuery.onlyGuidelineExperiments">Only Guideline Experiments</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('onlyGuidelineExperiments')} />
                </th>
                <th>
                  <Translate contentKey="coconApp.searchQuery.applications">Applications</Translate>
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {searchQueryList.map((searchQuery: ISearchQuery) => (
                <tr key={`entity-${searchQuery.id}`} data-cy="entityTable">
                  <td>{searchQuery.id}</td>
                  <td>
                    <td>{searchQuery.materials?.map(({ molecularFormula }) => molecularFormula).join(', ')}</td>
                  </td>
                  <td>{searchQuery.variant?.join(', ')}</td>
                  <td>{searchQuery.synthesis?.join(', ')}</td>
                  <td>{searchQuery.materialShape.map(shape => translate('coconApp.Shape.' + shape)).join(', ')}</td>
                  <td>{searchQuery.crystallinity}</td>
                  <td>{searchQuery.coatingOrFunctionalization}</td>
                  <td>{searchQuery.qualityScore?.map(qualityScore => translate('coconApp.QualityScore.' + qualityScore)).join(', ')}</td>

                  <td>
                    {searchQuery.materialSizeFrom && (
                      <>
                        {searchQuery.materialSizeFrom}&ndash;{searchQuery.materialSizeTo}
                      </>
                    )}
                  </td>
                  <td>
                    {searchQuery.agglomerationSizeFrom && (
                      <>
                        {searchQuery.agglomerationSizeFrom}&ndash;{searchQuery.agglomerationSizeTo}
                      </>
                    )}
                  </td>
                  <td>
                    {searchQuery.betFrom && (
                      <>
                        {searchQuery.betFrom}&ndash;{searchQuery.betTo}
                      </>
                    )}
                  </td>
                  <td>
                    <Translate contentKey={`coconApp.InVivoOrInVitro.${searchQuery.animalOrCell}`} />
                  </td>
                  <td>{searchQuery.origins?.map(({ name }) => name).join(', ')}</td>
                  <td>{searchQuery.cellCultures?.map(({ name }) => name).join(', ')}</td>
                  <td>{searchQuery.tissueTypes?.map(({ name }) => name).join(', ')}</td>
                  <td>{searchQuery.organs?.map(({ name }) => name).join(', ')}</td>
                  <td>{searchQuery.onlyGuidelineExperiments ? 'Yes' : ''}</td>
                  <td>{searchQuery.applications?.map(({ name }) => name).join(', ')}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/search-query/${searchQuery.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      {isAllowedToManageData && (
                        <Button
                          onClick={() => (location.href = `/search-query/${searchQuery.id}/delete`)}
                          color="danger"
                          size="sm"
                          data-cy="entityDeleteButton"
                        >
                          <FontAwesomeIcon icon="trash" />{' '}
                          <span className="d-none d-md-inline">
                            <Translate contentKey="entity.action.delete">Delete</Translate>
                          </span>
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="coconApp.searchQuery.home.notFound">No Search Queries found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={searchQueryList && searchQueryList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default SearchQuery;
